import {getBackendErrorMessage} from "../../shared/utils/httpError";
import {formatCurrencyBR} from "../../shared/utils/formatters";
import {buildPermissionTree} from "../Permissions/permissionsUtils";
export {extractListPayload} from "../../shared/utils/payload";

export const PLANO_STATUS_OPTIONS = ['ATIVO', 'INATIVO', 'DESCONTINUADO'];
export const TIPO_COBRANCA_OPTIONS = [
    {value: 'PRE_PAGO', label: 'Pre-pago', description: 'Cobranca antecipada'},
    {value: 'POS_PAGO', label: 'Pos-pago', description: 'Cobranca apos uso'},
];

export const formatDateTimeBR = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('pt-BR');
};

export const formatMoney = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) return '-';
    return new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2}).format(amount);
};

export const formatMoneyInput = (value) => {
    return formatCurrencyBR(value, false);
};

export const toNumberOrNull = (value) => {
    if (value === '' || value == null) return null;
    const raw = String(value).trim();
    if (!raw) return null;

    const sanitized = raw.replace(/[^\d,.-]/g, '');
    if (!sanitized) return null;

    let normalized = sanitized;
    if (sanitized.includes(',')) {
        normalized = sanitized.replace(/\./g, '').replace(',', '.');
    } else if (sanitized.includes('.')) {
        const dotCount = (sanitized.match(/\./g) || []).length;
        if (dotCount > 1) {
            normalized = sanitized.replace(/\./g, '');
        } else {
            const decimalCandidate = sanitized.split('.')[1] || '';
            normalized = decimalCandidate.length <= 2
                ? sanitized
                : sanitized.replace(/\./g, '');
        }
    }

    const parsed = Number(normalized);
    if (Number.isNaN(parsed)) return null;
    return parsed;
};

export const getBackendEntityMessage = (error, fallbackMessage) => getBackendErrorMessage(error, fallbackMessage);

// Desde a migration 0064 o módulo tem um sistema só, em `sistema_id`. As listas ficam de reserva até saírem da API.
export const getSystemIdFromModulo = (modulo) => {
    if (modulo?.sistema_id) {
        return modulo.sistema_id;
    }

    if (Array.isArray(modulo?.sistema_ids) && modulo.sistema_ids.length) {
        return modulo.sistema_ids[0];
    }

    if (Array.isArray(modulo?.sistemas) && modulo.sistemas.length) {
        return modulo.sistemas[0]?.id;
    }

    return modulo?.id_sistema || '';
};

export const normalizeModuloOptions = (items = []) => items.map((item) => ({
    id: item.id,
    nome: item.nome,
    descricao: item.descricao,
    id_sistema: getSystemIdFromModulo(item),
}));

export const normalizeListRows = (entityKey, items = []) => items.map((item) => {
    if (entityKey === 'planos') {
        return {
            ...item,
            nome: item.nome_plano,
            criado_em: formatDateTimeBR(item.criado_em),
            atualizado_em: formatDateTimeBR(item.atualizado_em),
        };
    }

    return {
        ...item,
        nome: item.nome_recurso,
        criado_em: formatDateTimeBR(item.criado_em),
        atualizado_em: formatDateTimeBR(item.atualizado_em),
    };
});

export const getTipoCobrancaLabel = (value) => TIPO_COBRANCA_OPTIONS.find((option) => option.value === value)?.label || value || '-';

/*
 * Perfis-modelo do plano (operador por tenant): o identity copia cada um para toda licença nova do plano.
 * O bloco sem recurso é "sem modelo" e vai como `null`. Os recursos têm de estar no direito do plano,
 * que são os módulos incluídos por padrão do próprio corpo do PUT; a API recusa o resto com 400 listando
 * os ids. No PUT, tipo ausente fica como está, objeto substitui e `null` remove; nome vazio vira o padrão.
 */
export const PERFIS_MODELO = [
    {
        tipo: 'operador',
        titulo: 'Operador',
        nomePadrao: 'Operador',
        semModelo: 'Sem modelo: as licenças deste plano não ganham usuário operador.',
    },
    {
        tipo: 'administrador',
        titulo: 'Administrador',
        nomePadrao: 'Administrador',
        semModelo: 'Sem modelo: o primeiro administrador recebe todo o direito do plano.',
    },
];

export const NOME_PERFIL_MODELO_MAX = 255;

const idDoRecurso = (item) => (typeof item === 'string' ? item : item?.id || item?.id_recurso || '');

export const normalizePerfisModelo = (raw) => PERFIS_MODELO.reduce((acc, {tipo, nomePadrao}) => {
    const perfil = raw?.[tipo];
    acc[tipo] = {
        nome: perfil?.nome ?? nomePadrao,
        recursos: Array.isArray(perfil?.recursos) ? [...new Set(perfil.recursos.map(idDoRecurso).filter(Boolean))] : [],
    };
    return acc;
}, {});

export const perfisModeloToPayload = (perfis) => PERFIS_MODELO.reduce((acc, {tipo, nomePadrao}) => {
    const perfil = perfis?.[tipo];
    acc[tipo] = perfil?.recursos?.length
        ? {nome: (perfil.nome || '').trim() || nomePadrao, recursos: [...perfil.recursos]}
        : null;
    return acc;
}, {});

const modulosInclusos = (form) => new Set((form?.modulos || [])
    .filter((item) => item.id_modulo && item.incluso_por_padrao)
    .map((item) => item.id_modulo));

// Ids do direito do plano como está no formulário: recursos das funcionalidades dos módulos incluídos por padrão.
export const idsNoDireitoDoPlano = (form, recursos = []) => {
    const inclusos = modulosInclusos(form);
    return new Set(recursos
        .filter((recurso) => inclusos.has(recurso.funcionalidade?.modulo_id))
        .map((recurso) => recurso.id));
};

const podarPerfisModelo = (perfis, recursosNoDireito) => (recursosNoDireito
    ? PERFIS_MODELO.reduce((acc, {tipo}) => {
        const perfil = perfis?.[tipo];
        acc[tipo] = perfil ? {...perfil, recursos: (perfil.recursos || []).filter((id) => recursosNoDireito.has(id))} : perfil;
        return acc;
    }, {})
    : perfis);

// A ordem dos recursos não conta: só nome e conjunto de recursos.
const assinaturaDoPerfil = (perfil) => (perfil ? JSON.stringify([perfil.nome, [...perfil.recursos].sort()]) : 'null');

/*
 * `perfis_modelo` do corpo, ou `null` para não mandar o campo. Vai só o tipo que mudou em relação ao
 * `original` (no POST, nenhum), já sem os recursos fora do direito quando ele é conhecido: um modelo que
 * perdeu o módulo sai limpo, e um que ficou sem recurso vai como `null` (remove).
 */
export const perfisModeloParaEnvio = (perfis, {original = null, recursosNoDireito = null} = {}) => {
    const atual = perfisModeloToPayload(podarPerfisModelo(perfis, recursosNoDireito));
    const antes = perfisModeloToPayload(original);
    const corpo = PERFIS_MODELO.reduce((acc, {tipo}) => {
        if (assinaturaDoPerfil(atual[tipo]) !== assinaturaDoPerfil(antes[tipo])) acc[tipo] = atual[tipo];
        return acc;
    }, {});
    return Object.keys(corpo).length ? corpo : null;
};

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/*
 * Ids que o 400 da API recusou, por bloco. Vale qualquer formato (lista em `valor`, objeto por perfil
 * ou ids na `mensagem`): conta só o id que está selecionado no bloco, para um 400 de outro campo
 * (um módulo inexistente, por exemplo) não virar recurso recusado.
 */
export const getRecursosRecusados = (error, perfis) => {
    if (error?.response?.status !== 400) return null;
    const citados = new Set((JSON.stringify(error.response.data ?? '').match(UUID) || []).map((id) => id.toLowerCase()));
    const recusados = PERFIS_MODELO.reduce((acc, {tipo}) => {
        acc[tipo] = (perfis?.[tipo]?.recursos || []).filter((id) => citados.has(String(id).toLowerCase()));
        return acc;
    }, {});
    return PERFIS_MODELO.some(({tipo}) => recusados[tipo].length) ? recusados : null;
};

const porNome = (a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR');

/*
 * Árvore módulo → funcionalidade → recurso do direito do plano, a partir do formulário (antes de salvar):
 * só os módulos do sistema marcados como incluídos por padrão. Os recursos vêm de `/recursos?sistema_id=`,
 * que trazem a funcionalidade (com o módulo) de cada um.
 */
export const buildDireitoDoPlano = ({form, modulos = [], recursos = []}) => {
    const funcionalidades = [...new Map(recursos
        .map((recurso) => recurso.funcionalidade)
        .filter((funcionalidade) => funcionalidade?.id)
        .map((funcionalidade) => [funcionalidade.id, funcionalidade])).values()].sort(porNome);

    return buildPermissionTree({
        modulos: modulos.filter((modulo) => modulo.id_sistema === form?.id_sistema),
        funcionalidades,
        recursos,
    }, {allowedModuloIds: modulosInclusos(form)});
};

export const describeRecurso = (recurso, id) => {
    if (!recurso) return `Recurso ${id}`;
    const metodo = recurso.metodo || recurso.httpMethod;
    return metodo || recurso.endpoint
        ? `${recurso.nome || recurso.resourceName || id} (${[metodo, recurso.endpoint].filter(Boolean).join(' ')})`
        : recurso.nome || recurso.resourceName || id;
};
